/**
 * AdmissionsLeadDetail — single-lead view.
 *
 * Shows full lead context, stage advance via dropdown, send-application
 * button, internal notes editor, stage history timeline, payments list +
 * mark-offline modal, signatures audit list.
 *
 * Mirrors the existing AdminPrivacy / AdminTranslationFeedback layouts —
 * one column of "section cards", each a rounded-2xl bordered panel.
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, Copy, ExternalLink, Plus } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";

const STAGE_FLOW = [
  "inquiry",
  "tour_booked",
  "application_sent",
  "application_received",
  "offer",
  "enrolled",
  "active",
  "withdrawn",
];

function stageLabel(t, s) {
  return t(`admissions.pipeline.stage_${s}`);
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}

export default function AdmissionsLeadDetail() {
  const { t } = useI18n();
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [internalNotes, setInternalNotes] = useState("");
  const [payments, setPayments] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/admissions/leads`, { params: {} });
      const found = (r.data || []).find((l) => l.id === id);
      if (!found) {
        toast.error("Lead not found.");
        nav("/admin/admissions");
        return;
      }
      setLead(found);
      setInternalNotes(found.internal_notes || "");
      const [pRes, sRes] = await Promise.all([
        api.get(`/admin/admissions/leads/${id}/payments`),
        api.get(`/admin/admissions/leads/${id}/signatures`),
      ]);
      setPayments(pRes.data || []);
      setSignatures(sRes.data || []);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const advance = async (newStage) => {
    try {
      const r = await api.patch(`/admin/admissions/leads/${id}/stage`, {
        stage: newStage,
      });
      setLead(r.data);
      toast.success(`→ ${stageLabel(t, newStage)}`);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    }
  };

  const sendApplication = async () => {
    try {
      const r = await api.post(`/admin/admissions/leads/${id}/send-application`);
      setLead(r.data);
      toast.success("Application link sent.");
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    }
  };

  const saveNotes = async () => {
    try {
      const r = await api.patch(`/admin/admissions/leads/${id}/notes`, {
        internal_notes: internalNotes,
      });
      setLead(r.data);
      toast.success(t("admissions.pipeline.saved"));
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    }
  };

  if (loading || !lead) {
    return <p className="text-sm text-[#6B7280] italic">{t("admissions.common.loading")}</p>;
  }

  const applyUrl = lead.application_token
    ? `${window.location.origin}/admissions/apply/${lead.application_token}`
    : null;

  return (
    <div data-testid="admin-admissions-lead-detail" className="space-y-6 max-w-4xl">
      <Link
        to="/admin/admissions"
        data-testid="admin-admissions-back"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F3A2A] hover:text-[#132C17]"
      >
        <ArrowLeft size={14} />
        {t("admissions.lead_detail.back_to_pipeline")}
      </Link>

      {/* Header */}
      <header className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
        <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1F3A2A]">
          {lead.desired_room_name || "Room TBD"}
        </div>
        <h1 className="mt-2 font-display text-3xl text-[#1A1B2E]">
          {lead.child_name}
        </h1>
        <p className="mt-2 text-sm text-[#6B7280]">
          {lead.parent_name} · {lead.parent_email}
          {lead.parent_phone && <> · {lead.parent_phone}</>}
        </p>

        {/* Stage advancer */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#6B7280]">
            {t("admissions.lead_detail.stage_label")}:
          </span>
          {STAGE_FLOW.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`admin-admissions-stage-${s}`}
              disabled={lead.stage === s}
              onClick={() => advance(s)}
              className={
                "text-xs font-semibold rounded-full px-3 py-1 transition-all " +
                (lead.stage === s
                  ? "bg-[#1F3A2A] text-white cursor-default"
                  : "bg-white border border-[#EEF0F4] text-[#1A1B2E] hover:border-[#1F3A2A]")
              }
            >
              {stageLabel(t, s)}
            </button>
          ))}
        </div>

        {/* Send application action */}
        {["inquiry", "tour_booked"].includes(lead.stage) && (
          <button
            type="button"
            data-testid="admin-admissions-send-application"
            onClick={sendApplication}
            className="mt-4 btn-primary !py-2 !px-4 !text-sm inline-flex items-center gap-2"
          >
            <Send size={14} />
            {t("admissions.lead_detail.send_application")}
          </button>
        )}

        {/* Application link */}
        {applyUrl && (
          <div className="mt-4 rounded-xl bg-[#F7F8FC] border border-[#EEF0F4] p-3">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#6B7280]">
              {t("admissions.lead_detail.application_link")}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 text-[11px] text-[#1A1B2E] break-all">{applyUrl}</code>
              <button
                type="button"
                data-testid="admin-admissions-copy-link"
                onClick={() => {
                  navigator.clipboard.writeText(applyUrl);
                  toast.success("Copied.");
                }}
                className="text-xs text-[#1F3A2A] hover:text-[#132C17] inline-flex items-center gap-1"
              >
                <Copy size={12} />
                {t("admissions.lead_detail.copy_link")}
              </button>
              <a
                href={applyUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="admin-admissions-open-link"
                className="text-xs text-[#1F3A2A] hover:text-[#132C17] inline-flex items-center gap-1"
              >
                <ExternalLink size={12} />
                {t("admissions.lead_detail.open_in_new_tab")}
              </a>
            </div>
            {lead.application_token_expires_at && (
              <p className="mt-2 text-[10px] text-[#9CA3AF]">
                {t("admissions.lead_detail.link_expires", { date: formatDate(lead.application_token_expires_at) })}
              </p>
            )}
          </div>
        )}
      </header>

      {/* Notes from family */}
      {lead.notes && (
        <section className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
          <div className="eyebrow">{t("admissions.lead_detail.notes_section")}</div>
          <p className="mt-3 text-sm text-[#1A1B2E] whitespace-pre-wrap leading-relaxed">
            {lead.notes}
          </p>
        </section>
      )}

      {/* Internal notes */}
      <section className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
        <label className="block">
          <span className="eyebrow">{t("admissions.pipeline.internal_notes")}</span>
          <textarea
            data-testid="admin-admissions-internal-notes"
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
            rows={3}
            maxLength={2000}
            className="mt-3 input-soft w-full text-sm"
            placeholder="Private to admins…"
          />
        </label>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            data-testid="admin-admissions-save-notes"
            onClick={saveNotes}
            disabled={internalNotes === (lead.internal_notes || "")}
            className="text-xs font-semibold text-[#1F3A2A] disabled:text-[#9CA3AF] underline underline-offset-4"
          >
            {t("admissions.pipeline.save_notes")}
          </button>
        </div>
      </section>

      {/* Stage history */}
      <section className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
        <div className="eyebrow">{t("admissions.lead_detail.history")}</div>
        <ol
          data-testid="admin-admissions-history"
          className="mt-4 space-y-3 border-l-2 border-[#EEF0F4] ps-4"
        >
          {(lead.stage_history || []).map((h, i) => (
            <li key={i} className="text-sm">
              <span className="font-semibold text-[#1A1B2E]">{stageLabel(t, h.stage)}</span>
              <span className="ml-2 text-xs text-[#6B7280]">{formatDate(h.changed_at)}</span>
              {h.by_user_name && (
                <span className="ml-2 text-xs text-[#9CA3AF]">· {h.by_user_name}</span>
              )}
              {h.note && (
                <p className="mt-1 text-xs text-[#6B7280] italic">{h.note}</p>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* Payments */}
      <section className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="eyebrow">{t("admissions.lead_detail.payments")}</div>
          <button
            type="button"
            data-testid="admin-admissions-mark-payment"
            onClick={() => setShowPaymentModal(true)}
            className="text-xs font-semibold text-[#1F3A2A] inline-flex items-center gap-1 underline underline-offset-4"
          >
            <Plus size={12} />
            {t("admissions.lead_detail.mark_payment")}
          </button>
        </div>
        {payments.length === 0 ? (
          <p className="mt-4 text-sm text-[#6B7280] italic">No payments recorded.</p>
        ) : (
          <ul className="mt-4 space-y-2" data-testid="admin-admissions-payments">
            {payments.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-xl bg-[#F7F8FC] border border-[#EEF0F4] p-3 text-sm"
              >
                <div>
                  <div className="font-semibold text-[#1A1B2E]">
                    {p.amount.toFixed(2)} {p.currency}
                  </div>
                  <p className="text-xs text-[#6B7280] capitalize">
                    {p.type.replace(/_/g, " ")} · {p.method.replace(/_/g, " ")}
                  </p>
                  {p.note && (
                    <p className="text-xs text-[#9CA3AF] mt-1 italic">{p.note}</p>
                  )}
                </div>
                <div className="text-[10px] text-[#9CA3AF] text-right">
                  <div>{formatDate(p.received_at)}</div>
                  <div>{p.marked_by_name}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Signatures */}
      {signatures.length > 0 && (
        <section className="rounded-2xl border border-[#EEF0F4] bg-white p-6">
          <div className="eyebrow">{t("admissions.lead_detail.signatures")}</div>
          <ul className="mt-4 space-y-2" data-testid="admin-admissions-signatures">
            {signatures.map((s) => (
              <li
                key={s.id}
                className="rounded-xl bg-[#F7F8FC] border border-[#EEF0F4] p-3 text-sm"
              >
                <div className="font-semibold text-[#1A1B2E]">{s.doc_title_snapshot}</div>
                <p className="text-xs text-[#6B7280]">
                  Signed by {s.signer_name} · {formatDate(s.signed_at)}
                </p>
                <p className="text-[10px] text-[#9CA3AF] font-mono mt-1">
                  Hash: {s.doc_body_hash.slice(0, 16)}… · IP: {s.ip_address} · {s.signature_method.replace(/_/g, " ")}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Payment modal */}
      {showPaymentModal && (
        <PaymentModal
          leadId={id}
          onClose={() => setShowPaymentModal(false)}
          onCreated={(p) => {
            setPayments((prev) => [p, ...prev]);
            setShowPaymentModal(false);
          }}
          t={t}
        />
      )}
    </div>
  );
}

function PaymentModal({ leadId, onClose, onCreated, t }) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [type, setType] = useState("deposit");
  const [method, setMethod] = useState("bank_transfer");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a positive amount.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/admin/admissions/leads/${leadId}/mark-payment`, {
        amount: amt,
        currency,
        type,
        method,
        note: note.trim() || null,
      });
      toast.success("Payment recorded.");
      onCreated(r.data);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="admin-admissions-payment-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F2018]/55 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-white border border-[#EEF0F4] p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow">{t("admissions.lead_detail.mark_payment_modal_title")}</div>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">{t("admissions.lead_detail.amount")}</span>
            <input
              data-testid="admin-admissions-payment-amount"
              type="number"
              step="0.01"
              min="0.01"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="input-soft w-full"
            />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">{t("admissions.lead_detail.currency")}</span>
            <select
              data-testid="admin-admissions-payment-currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="input-soft w-full"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="DKK">DKK</option>
              <option value="NOK">NOK</option>
              <option value="SEK">SEK</option>
              <option value="SAR">SAR</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">{t("admissions.lead_detail.payment_type")}</span>
            <select
              data-testid="admin-admissions-payment-type"
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="input-soft w-full"
            >
              <option value="application_fee">Application fee</option>
              <option value="deposit">Deposit</option>
              <option value="tuition_prepay">Tuition prepay</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">{t("admissions.lead_detail.payment_method")}</span>
            <select
              data-testid="admin-admissions-payment-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="input-soft w-full"
            >
              <option value="bank_transfer">Bank transfer</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="card_offline">Card (offline)</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">{t("admissions.lead_detail.payment_note")}</span>
            <textarea
              data-testid="admin-admissions-payment-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={600}
              className="input-soft w-full"
            />
          </label>
          <div className="flex items-center justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm text-[#6B7280] px-4 py-2">
              Cancel
            </button>
            <button
              type="submit"
              data-testid="admin-admissions-payment-submit"
              disabled={sending}
              className="btn-primary !py-2 !px-5 !text-sm"
            >
              {t("admissions.lead_detail.save_payment")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
